import { useCallback, useState } from 'react';
import { RecurringDonationLandingSchoolChooseBox } from './RecurringDonationLandingSchoolChooseBox';
import { RecurringDonationLandingTeacherChooseBox } from './RecurringDonationLandingTeacherChooseBox';
import { GIVING_TIERS, RecurringDonationAmountButtons } from './RecurringDonationAmountButtons';
import { SchoolSuggestion, TeacherSuggestion } from '../../search/types/typeaheadSuggestions';
import { giveToSchool } from '../../common/giveToSchool';
import { getCsrfToken } from '../../utils/getCsrfToken';
import { RECURRING_DONATION_LANDING_PAGE } from '../../cart/donationSource';

type SelectionType = null | 'school' | 'teacher' | 'credits';

interface RecurringDonationLandingProps {
    highDefaultValues: boolean,
    initialCustomAmount?: number,
}

const HIGH_GIVING_TIERS = [100, 250, 500, 1000];
const chooseGivingTiers = (highDefaultValues: boolean): number[] => {
    if (highDefaultValues) {
        return HIGH_GIVING_TIERS;
    } else {
        return GIVING_TIERS;
    }
}

export const RecurringDonationLanding = function ({ highDefaultValues, initialCustomAmount }: RecurringDonationLandingProps) {
    const [giveAmount, setGiveAmount] = useState<number | null>(initialCustomAmount ?? chooseGivingTiers(highDefaultValues)[1]);
    const [isCreditsPurchase, setIsCreditsPurchase] = useState(false);
    const [selectionType, setSelectionType] = useState<SelectionType>(null); // we're  gonna gather data on this and preselect at a later time
    const [designatedSchool, setDesignatedSchool] = useState<SchoolSuggestion | null>(null);
    const [designatedTeacher, setDesignatedTeacher] = useState<TeacherSuggestion | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleChooseSchool = useCallback(() => {
        setSelectionType('school');
        setIsCreditsPurchase(false);
    }, []);

    const handleChooseTeacher = useCallback(() => {
        setSelectionType('teacher');
        setIsCreditsPurchase(false);
    }, []);

    const handleChooseLater = useCallback(() => {
        setSelectionType('credits');
        setIsCreditsPurchase(true);
    }, []);

    const handleSchoolOrTeacherSelect = useCallback((selection: {
        schoolSuggestion: SchoolSuggestion | null;
        teacherSuggestion: TeacherSuggestion | null;
    }) => {
        if (selection.schoolSuggestion) {
            setSelectionType('school');
        } else if (selection.teacherSuggestion) {
            setSelectionType('teacher');
        }
        setDesignatedSchool(selection.schoolSuggestion);
        setDesignatedTeacher(selection.teacherSuggestion);
    }, []);

    const reInitializeState = () => {
        setGiveAmount(null);
        setIsCreditsPurchase(false);
        setDesignatedSchool(null);
        setDesignatedTeacher(null);
    };

    const onSuccess = () => {
        reInitializeState();
        window.location.href = secureBaseURL + 'donors/givingCart.html';
    };
    const onFailure = () => {
        setErrorMessage('There was an error processing your donation request. Please refresh the page and try again.');
    };

    const giveToTeacher = () => {
        $.ajax({
            url: secureBaseURL + 'donors/recurring_donation_landing.json?recurringDonation=true&gcToTeacher=' + designatedTeacher?.teacherId,
            method: 'POST',
            headers: {
                [csrfTokenHeaderName]: getCsrfToken(),
            },
            data: {
                donationAmount: giveAmount,
                donationSource: RECURRING_DONATION_LANDING_PAGE,
            },
            xhrFields: {
                withCredentials: true,
            },
        }).done((data) => {
            onSuccess();
        }).fail((data) => {
            onFailure();
        });
    };

    const giveCreditsRecurringPurchase = () => {
        $.ajax({
            url: secureBaseURL + 'donors/recurring_donation_landing.json?recurringDonation=true',
            method: 'POST',
            headers: {
                [csrfTokenHeaderName]: getCsrfToken(),
            },
            data: {
                donationAmount: giveAmount,
                donationSource: RECURRING_DONATION_LANDING_PAGE,
            },
            xhrFields: {
                withCredentials: true,
            },
        }).done((data) => {
            onSuccess();
        }).fail((data) => {
            onFailure();
        });
    };

    const addToCart = () => {
        if (!giveAmount || giveAmount < 0) {
            setErrorMessage('Please enter a valid amount');
        } else {
            if (selectionType === 'school' && designatedSchool) {
                giveToSchool(designatedSchool.schoolId, giveAmount, true, onSuccess, onFailure, RECURRING_DONATION_LANDING_PAGE);
            } else if (selectionType === 'teacher' && designatedTeacher) {
                giveToTeacher();
            } else if (selectionType === 'credits') {
                giveCreditsRecurringPurchase();
            }
        }
    };

    const hasValidAmountAndTarget = () => {
        return (
            giveAmount !== null &&
            giveAmount > 0 &&
            (isCreditsPurchase || designatedSchool !== null || designatedTeacher !== null)
        );
    };

    return (
        <div className="container">
            <div className="recurring-donation-landing">
                <h1>Give monthly and multiply your impact</h1>
                <p className='subheader'>As a teacher-founded nonprofit, DonorsChoose is trusted by thousands of teachers and supporters across the country. <a className='subtle-link' href={baseURL + 'about'}>Learn more</a></p>
                <div className="recurring-donation-landing-wrapper">
                    <div className="recurring-donation-landing-container">
                        <p className="subheader">
                            Setting up a monthly donation makes it easy to plan and track your giving, while still letting you
                            support the classrooms that matter most to you.{' '} <b>Edit or cancel anytime.</b>
                        </p>
                        <div className="suggested-give-amounts">
                            <RecurringDonationAmountButtons
                                selectedAmount={giveAmount}
                                setSelectedAmount={setGiveAmount}
                                setErrorMessage={setErrorMessage}
                                customGivingTiers={chooseGivingTiers(highDefaultValues)}
                                initialCustomAmount={initialCustomAmount}
                            />
                            {errorMessage ? <div className="warningtext">{errorMessage}</div> : null}
                        </div>
                        <p style={{ textAlign: 'center' }}>How would you like to use your donation?</p>
                        <div className="recurring-donation-usage-options">
                            {/* Support a school option */}
                            <div className={`recurring-donation-option ${selectionType === 'school' ? 'selected' : ''}`}>
                                <label aria-label={'Choose a School'} className="js-click-on-enter-or-space" tabIndex={0}>
                                    <input
                                        type="radio"
                                        name="recurring-donation-usage"
                                        checked={selectionType === 'school'}
                                        onChange={handleChooseSchool}
                                        style={{ display: 'none' }}
                                    />
                                    <div className="option-content">
                                        <p className="option-title">Choose a school</p>
                                        <p className="option-description">
                                            You&apos;ll give to the same school every month.
                                        </p>
                                    </div>
                                </label>
                                <div className="recurring-donation-choose-now-content">
                                    {selectionType === 'school' && (
                                        <RecurringDonationLandingSchoolChooseBox
                                            designatedSchool={designatedSchool}
                                            handleSchoolOrTeacherSelect={handleSchoolOrTeacherSelect}
                                            setErrorMessage={setErrorMessage}
                                        />)}
                                </div>
                            </div>
                            {/* Support a teacher option */}
                            <div className={`recurring-donation-option ${selectionType === 'teacher' ? 'selected' : ''}`}>
                                <label aria-label={'Choose a Teacher'} className="js-click-on-enter-or-space" tabIndex={0}>
                                    <input
                                        type="radio"
                                        name="recurring-donation-usage"
                                        checked={selectionType === 'teacher'}
                                        onChange={handleChooseTeacher}
                                        style={{ display: 'none' }}
                                    />
                                    <div className="option-content">
                                        <p className="option-title">Choose a teacher</p>
                                        <p className="option-description">
                                        You&apos;ll give to the same teacher every month.
                                        </p>
                                    </div>
                                </label>
                                <div className="recurring-donation-choose-now-content">
                                    {selectionType === 'teacher' && (
                                        <RecurringDonationLandingTeacherChooseBox
                                            designatedTeacher={designatedTeacher}
                                            handleSchoolOrTeacherSelect={handleSchoolOrTeacherSelect}
                                        />)}
                                </div>
                            </div>
                            {/* Choose later option */}
                            <div className={`recurring-donation-option ${selectionType === 'credits' ? 'selected' : ''}`}>
                                <label aria-label={'Choose later'} className="js-click-on-enter-or-space" tabIndex={0}>
                                    <input
                                        type="radio"
                                        name="recurring-donation-usage"
                                        checked={selectionType === 'credits'}
                                        onChange={handleChooseLater}
                                        style={{ display: 'none' }}
                                    />
                                    <div className="option-content">
                                        <p className="option-title">Choose a project</p>
                                        <p className="option-description">
                                            You&apos;ll pick a different project every month.
                                        </p>
                                    </div>
                                </label>
                            </div>
                        </div>
                        <button className="button-primary full-width recurring-donation-submit" disabled={!hasValidAmountAndTarget()} onClick={addToCart}>
                            {giveAmount ? `Give $${giveAmount} monthly` : 'Give Monthly'}
                        </button>

                        <p className="recurring-donation-disclaimer">
                            We&#39;ll charge you once today. Starting next month, you&#39;ll be charged on the 17th of every month.
                        </p>
                    </div>
                    <div className="recurring-donation-landing-image" aria-label="Monthly giving calendar"></div>
                </div>
            </div>
            <div className='dc-info'>
                <img src='/images/funds/trophy.svg' alt=''/>
                <h1>Most trusted by teachers</h1>
                <p className='subheader'>As a teacher-founded nonprofit, DonorsChoose is trusted by thousands of teachers and supporters across the country.</p>
                <div>
                    <a className='button-secondary button-overlay' href={baseURL + 'about'}> Learn about DonorsChoose </a>
                    <p className='text-discreet'>If you&#39;re a teacher, <a href={baseURL + 'teachers'}>get funded!</a></p>
                </div>
            </div>
            <div className='learn-more-container'>
                <a className='learn-more-1 emphasis' href={baseURL + 'about'}>
                    <div>
                        <img src='/images/funds/learn-more-1.svg' alt=''/>
                        <span className='subtle-link'>Learn more</span>
                    </div>
                    <p className='emphasis'>DonorsChoose works with schools and districts directly.</p>
                </a>
                <a className='learn-more-2 emphasis' href={baseURL + 'about/finance.html'}>
                    <div>
                        <img src='/images/funds/learn-more-2.svg' alt=''/>
                        <span className='subtle-link'>Learn more</span>
                    </div>
                    <p>100% of your donation is tax-deductible!</p>
                </a>
            </div>
        </div>
    );
};
